import React, { useState, useEffect } from "react";
import BookingForm from "./BookingForm";
import HourlyBookingForm from "./HourlyBookingForm";

function HonestLimo() {
  const driverData = {
    name: "HonestLimo",
    phoneNumber: "952-652-3891",
    email: "honestlimoservice@gmail.com",
    costPerMile: 4.25,
    costPerMileSedan: 3.75,
    minRideCost: 70,
    startCost: 15,
  };
  const [isHourlyBooking, setIsHourlyBooking] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleCallButtonClick = () => {
    window.location.href = "tel:9526523891";
  };

  const scrollToBookingForm = () => {
    const bookingForm = document.getElementById("bookingFormSection");
    if (bookingForm) {
      bookingForm.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="min-h-screen bg-honest-bg grain" id="top">
      {/* Nav */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-honest-bg/95 backdrop-blur-md border-b border-honest-border" : "bg-transparent"
        }`}
      >
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <a href="#top" className="flex items-center gap-3 group" aria-label="Honest Limo home">
            <img
              src={require("./logo.png")}
              alt="Honest Limo"
              className="h-11 w-auto object-contain"
            />
            <span className="font-display text-2xl font-semibold text-honest-cream tracking-tight">
              Honest Limo
            </span>
          </a>
          <div className="flex items-center gap-3 sm:gap-4">
            <a
              href={`tel:${driverData.phoneNumber.replace(/-/g, "")}`}
              onClick={handleCallButtonClick}
              className="hidden sm:inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-honest-gold/50 text-honest-gold hover:bg-honest-gold/10 transition-colors font-medium text-sm"
            >
              <span aria-hidden>📞</span> Call
            </a>
            <button
              onClick={scrollToBookingForm}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-honest-gold text-honest-bg font-semibold hover:bg-honest-gold-light transition-colors text-sm"
            >
              Book Now
            </button>
          </div>
        </nav>
      </header>

      {/* Hero */}
      <section className="relative min-h-[100vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat scale-105"
          style={{
            backgroundImage: `url(https://simonslimo.com/wp-content/uploads/elementor/thumbs/2018-chevrolet-suburban-101-1531774571-1-qkf7pczgmkbrmm05uos181djqsmajzzt0v95wyazvk.jpg)`,
          }}
        />
        <div className="absolute inset-0 bg-hero-gradient" />
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 text-center pt-20">
          <p className="font-display text-honest-gold text-lg sm:text-xl tracking-[0.2em] uppercase mb-4 opacity-0 animate-fade-in" style={{ animationDelay: "100ms", animationFillMode: "forwards" }}>
            Minnesota’s Premier Chauffeur Service
          </p>
          <h1 className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-semibold text-honest-cream leading-[1.05] tracking-tight mb-6 opacity-0 animate-slide-up" style={{ animationDelay: "200ms", animationFillMode: "forwards" }}>
            Travel in
            <br />
            <span className="text-honest-gold italic">Elegance</span>
          </h1>
          <p className="text-honest-cream-muted text-lg sm:text-xl max-w-2xl mx-auto mb-10 opacity-0 animate-slide-up" style={{ animationDelay: "300ms", animationFillMode: "forwards" }}>
            Airport transfers, corporate travel, and special events across the Twin Cities. Arrive in comfort and style—proudly Minnesota-based.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center opacity-0 animate-slide-up" style={{ animationDelay: "500ms", animationFillMode: "forwards" }}>
            <button
              onClick={scrollToBookingForm}
              className="px-8 py-4 rounded-full bg-honest-gold text-honest-bg font-semibold text-lg hover:bg-honest-gold-light transition-all hover:scale-[1.02]"
            >
              Reserve Your Ride
            </button>
            <a
              href={`tel:${driverData.phoneNumber.replace(/-/g, "")}`}
              onClick={handleCallButtonClick}
              className="px-8 py-4 rounded-full border-2 border-honest-cream/40 text-honest-cream font-semibold hover:border-honest-gold hover:text-honest-gold transition-colors"
            >
              {driverData.phoneNumber}
            </a>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="relative py-24 sm:py-32 px-4 sm:px-6 bg-honest-surface">
        <div className="max-w-7xl mx-auto">
          <p className="font-display text-honest-gold text-sm tracking-[0.2em] uppercase mb-2">What We Offer</p>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold text-honest-cream mb-4">
            Our Services
          </h2>
          <p className="text-honest-cream-muted text-lg max-w-2xl mb-16">
            From MSP to Minneapolis, St. Paul, and beyond—first-class ground transportation across the Land of 10,000 Lakes.
          </p>
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {[
              {
                title: "Airport Transfers",
                desc: "Stress-free airport transportation. We track your flight and ensure a seamless pickup and drop-off.",
                image: "https://simons.limo/wp-content/uploads/2022/11/chauffeur-standing-by-the-vehicle-with-the-open-bo-2021-09-03-09-42-18-utc.jpg",
                accent: "From MSP & beyond",
              },
              {
                title: "Corporate Travel",
                desc: "Punctual, professional rides for business. Impress clients and focus on what matters.",
                image: "https://simons.limo/wp-content/uploads/2022/11/two-businesspeople-sitting-inside-car-using-electronic-devices-3874150-1024x683-1.jpg",
                accent: "On time, every time",
              },
              {
                title: "Events & Celebrations",
                desc: "Weddings, proms, and special occasions. Arrive in style and make the moment unforgettable.",
                image: "https://simons.limo/wp-content/uploads/2022/11/happy-couple-transporting-via-a-car-2022-09-16-07-34-30-utc.jpg",
                accent: "Make it memorable",
              },
            ].map((item, i) => (
              <article
                key={i}
                className="group relative rounded-2xl overflow-hidden bg-honest-surface-elevated border border-white/5 hover:border-honest-gold/30 transition-all duration-300"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={item.image}
                    alt=""
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-honest-bg via-transparent to-transparent opacity-80" />
                </div>
                <div className="p-6 sm:p-8">
                  <p className="text-honest-gold text-sm font-medium mb-1">{item.accent}</p>
                  <h3 className="font-display text-2xl font-semibold text-honest-cream mb-3">
                    {item.title}
                  </h3>
                  <p className="text-honest-cream-muted leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="relative py-24 sm:py-32 px-4 sm:px-6 bg-honest-bg">
        <div className="max-w-7xl mx-auto text-center">
          <p className="font-display text-honest-gold text-sm tracking-[0.2em] uppercase mb-2">The Honest Difference</p>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold text-honest-cream mb-6">
            Why Choose Us
          </h2>
          <p className="text-honest-cream-muted text-lg max-w-2xl mx-auto mb-16">
            A Minnesota-based team that treats every trip like it matters—because it does.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                  </svg>
                ),
                title: "Professional Service",
                text: "First-class ground transportation with trained chauffeurs and well-maintained vehicles.",
              },
              {
                icon: (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                ),
                title: "Safety First",
                text: "Your safety is our priority. Inspected vehicles and experienced, courteous drivers.",
              },
              {
                icon: (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                ),
                title: "Easy Booking",
                text: "Book online, call, or text. We respond quickly and confirm every reservation.",
              },
              {
                icon: (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ),
                title: "Reliable & On Time",
                text: "We handle the logistics so you and your guests arrive in comfort, on schedule.",
              },
            ].map((item, i) => (
              <div
                key={i}
                className="p-6 rounded-2xl bg-honest-surface border border-white/5 hover:border-honest-gold/20 transition-colors"
              >
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-honest-gold/15 text-honest-gold mb-4">
                  {item.icon}
                </div>
                <h3 className="font-display text-xl font-semibold text-honest-cream mb-2">
                  {item.title}
                </h3>
                <p className="text-honest-cream-muted text-sm leading-relaxed">
                  {item.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Booking */}
      <section
        id="bookingFormSection"
        className="relative py-24 sm:py-32 px-4 sm:px-6 bg-honest-surface"
      >
        <div className="max-w-2xl mx-auto">
          <p className="font-display text-honest-gold text-sm tracking-[0.2em] uppercase mb-2 text-center">
            Reserve
          </p>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold text-honest-cream mb-4 text-center">
            Make Your Reservation
          </h2>
          <p className="text-honest-cream-muted text-center mb-10">
            Distance-based or hourly—we serve the Twin Cities and beyond. We’ll confirm by call or text.
          </p>

          {/* Booking type toggle */}
          <div className="flex justify-center gap-2 p-1.5 rounded-full bg-honest-surface-elevated border border-white/10 mb-10 max-w-xs mx-auto">
            <button
              type="button"
              onClick={() => setIsHourlyBooking(false)}
              className={`flex-1 py-2.5 px-4 rounded-full text-sm font-medium transition-all ${
                !isHourlyBooking
                  ? "bg-honest-gold text-honest-bg"
                  : "text-honest-cream-muted hover:text-honest-cream"
              }`}
            >
              By Distance
            </button>
            <button
              type="button"
              onClick={() => setIsHourlyBooking(true)}
              className={`flex-1 py-2.5 px-4 rounded-full text-sm font-medium transition-all ${
                isHourlyBooking
                  ? "bg-honest-gold text-honest-bg"
                  : "text-honest-cream-muted hover:text-honest-cream"
              }`}
            >
              Hourly
            </button>
          </div>

          {isHourlyBooking ? (
            <HourlyBookingForm driverData={driverData} />
          ) : (
            <BookingForm driverData={driverData} />
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="relative py-12 sm:py-16 px-4 sm:px-6 bg-honest-bg border-t border-white/5">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <img
              src={require("./logo.png")}
              alt="Honest Limo"
              className="h-10 w-auto object-contain"
            />
            <span className="font-display text-xl font-semibold text-honest-cream">
              Honest Limo
            </span>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-6 text-honest-cream-muted text-sm">
            <a
              href={`tel:${driverData.phoneNumber.replace(/-/g, "")}`}
              onClick={handleCallButtonClick}
              className="hover:text-honest-gold transition-colors"
            >
              {driverData.phoneNumber}
            </a>
            <a
              href={`mailto:${driverData.email}`}
              className="hover:text-honest-gold transition-colors"
            >
              {driverData.email}
            </a>
          </div>
        </div>
        <div className="max-w-7xl mx-auto mt-8 pt-8 border-t border-white/5 text-center text-honest-cream-muted text-sm">
          © {new Date().getFullYear()} Honest Limo. Proudly serving the Twin Cities & Minnesota.
        </div>
      </footer>
    </div>
  );
}

export default HonestLimo;
